import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { LocationOption, Event } from '../types';

interface LocationDetailEditorProps {
  location: LocationOption;
  onSave: (location: LocationOption) => void;
  onClose: () => void;
  open: boolean;
}

export function LocationDetailEditor({ 
  location,
  onSave,
  onClose,
  open
}: LocationDetailEditorProps) {
  const [editingLocation, setEditingLocation] = useState<LocationOption>(location);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Edit Location</DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="max-h-[70vh] pr-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Location ID</Label>
              <Input
                value={editingLocation.locationId}
                onChange={(e) => setEditingLocation({
                  ...editingLocation,
                  locationId: e.target.value
                })}
                placeholder="Unique location identifier"
              />
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={editingLocation.title}
                onChange={(e) => setEditingLocation({
                  ...editingLocation,
                  title: e.target.value
                })}
              />
            </div>
            <div className="space-y-2">
              <Label>Subtitle</Label>
              <Input
                value={editingLocation.subtitle}
                onChange={(e) => setEditingLocation({
                  ...editingLocation,
                  subtitle: e.target.value
                })}
              />
            </div>
            <div className="space-y-2">
              <Label>Label (Optional)</Label>
              <Input
                value={editingLocation.label || ''}
                onChange={(e) => setEditingLocation({
                  ...editingLocation,
                  label: e.target.value || undefined
                })}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={editingLocation.description}
                onChange={(e) => setEditingLocation({
                  ...editingLocation,
                  description: e.target.value
                })}
              />
            </div>
            <div className="space-y-2">
              <Label>Image URL (Optional)</Label>
              <Input
                value={editingLocation.imgUrl || ''}
                onChange={(e) => setEditingLocation({
                  ...editingLocation,
                  imgUrl: e.target.value || null
                })}
                placeholder="https://example.com/image.jpg"
              />
            </div>
            <div className="space-y-2">
              <Label>URL (Optional)</Label>
              <Input
                value={editingLocation.url || ''}
                onChange={(e) => setEditingLocation({
                  ...editingLocation,
                  url: e.target.value || null
                })}
                placeholder="https://example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Detail Content</Label>
              <div className="space-y-2">
                {(editingLocation.detail_content || []).map((detail, index) => (
                  <div key={index} className="space-y-2 p-4 border rounded-lg">
                    <div className="flex justify-between items-center">
                      <Label>Content Type</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newLocation = { ...editingLocation };
                          newLocation.detail_content = (newLocation.detail_content || []).filter((_, i) => i !== index);
                          setEditingLocation(newLocation);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <select
                      value={detail.type}
                      onChange={(e) => {
                        const newLocation = { ...editingLocation };
                        const type = e.target.value as Event["Chat"] | Event["Html"] | Event["Embed"] | Event["Map"] | Event["Location"];
                        let content: any = {};
                        
                        // Initialize appropriate content structure based on type
                        switch (type) {
                          case "CHAT":
                          case "HTML":
                            content = "";
                            break;
                          case "EMBED":
                            content = { url: "" };
                            break;
                          case "MAP":
                            content = { poiId: 0, state: null };
                            break;
                          case "LOCATION":
                            content = {
                              type: "LOCATION",
                              title: "",
                              subtitle: "",
                              description: "",
                              locationId: "",
                            };
                            break;
                        }
                        
                        newLocation.detail_content = [
                          ...(newLocation.detail_content || []).slice(0, index),
                          { type, content },
                          ...(newLocation.detail_content || []).slice(index + 1)
                        ];
                        setEditingLocation(newLocation);
                      }}
                      className="w-full p-2 border rounded-md"
                    >
                      <option value="CHAT">Chat</option>
                      <option value="HTML">HTML</option>
                      <option value="EMBED">Embed</option>
                      <option value="MAP">Map</option>
                      <option value="LOCATION">Location</option>
                    </select>
                    
                    {/* Content editor based on type */}
                    {detail.type === "CHAT" || detail.type === "HTML" ? (
                      <Textarea
                        value={detail.content}
                        onChange={(e) => {
                          const newLocation = { ...editingLocation };
                          newLocation.detail_content = [
                            ...(newLocation.detail_content || []).slice(0, index),
                            { ...detail, content: e.target.value },
                            ...(newLocation.detail_content || []).slice(index + 1)
                          ];
                          setEditingLocation(newLocation);
                        }}
                        placeholder={`Enter ${detail.type.toLowerCase()} content...`}
                        className="mt-2"
                      />
                    ) : detail.type === "EMBED" ? (
                      <div className="space-y-2 mt-2">
                        <Label>URL</Label>
                        <Input
                          value={detail.content.url}
                          onChange={(e) => {
                            const newLocation = { ...editingLocation };
                            newLocation.detail_content = [
                              ...(newLocation.detail_content || []).slice(0, index),
                              { 
                                ...detail, 
                                content: { 
                                  ...detail.content,
                                  url: e.target.value 
                                }
                              },
                              ...(newLocation.detail_content || []).slice(index + 1)
                            ];
                            setEditingLocation(newLocation);
                          }}
                          placeholder="Enter embed URL..."
                        />
                      </div>
                    ) : detail.type === "MAP" ? (
                      <div className="space-y-2 mt-2">
                        <div className="space-y-2">
                          <Label>POI ID</Label>
                          <Input
                            type="number"
                            value={detail.content.poiId}
                            onChange={(e) => {
                              const newLocation = { ...editingLocation };
                              newLocation.detail_content = [
                                ...(newLocation.detail_content || []).slice(0, index),
                                { 
                                  ...detail, 
                                  content: { 
                                    ...detail.content,
                                    poiId: parseInt(e.target.value) || 0
                                  }
                                },
                                ...(newLocation.detail_content || []).slice(index + 1)
                              ];
                              setEditingLocation(newLocation);
                            }}
                            placeholder="Enter POI ID..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>State</Label>
                          <Input
                            value={detail.content.state || ''}
                            onChange={(e) => {
                              const newLocation = { ...editingLocation };
                              newLocation.detail_content = [
                                ...(newLocation.detail_content || []).slice(0, index),
                                { 
                                  ...detail, 
                                  content: { 
                                    ...detail.content,
                                    state: e.target.value || null
                                  }
                                },
                                ...(newLocation.detail_content || []).slice(index + 1)
                              ];
                              setEditingLocation(newLocation);
                            }}
                            placeholder="Enter map state..."
                          />
                        </div>
                      </div>
                    ) : detail.type === "LOCATION" ? (
                      <div className="space-y-2 mt-2">
                        <div className="space-y-2">
                          <Label>Location ID</Label>
                          <Input
                            value={detail.content.locationId}
                            onChange={(e) => {
                              const newLocation = { ...editingLocation };
                              newLocation.detail_content = [
                                ...(newLocation.detail_content || []).slice(0, index),
                                { 
                                  ...detail, 
                                  content: { 
                                    ...detail.content,
                                    locationId: e.target.value
                                  }
                                },
                                ...(newLocation.detail_content || []).slice(index + 1)
                              ];
                              setEditingLocation(newLocation);
                            }}
                            placeholder="Enter location ID..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Title</Label>
                          <Input
                            value={detail.content.title}
                            onChange={(e) => {
                              const newLocation = { ...editingLocation };
                              newLocation.detail_content = [
                                ...(newLocation.detail_content || []).slice(0, index),
                                { 
                                  ...detail, 
                                  content: { 
                                    ...detail.content,
                                    title: e.target.value
                                  }
                                },
                                ...(newLocation.detail_content || []).slice(index + 1)
                              ];
                              setEditingLocation(newLocation);
                            }}
                            placeholder="Enter location title..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Subtitle</Label>
                          <Input
                            value={detail.content.subtitle}
                            onChange={(e) => {
                              const newLocation = { ...editingLocation };
                              newLocation.detail_content = [
                                ...(newLocation.detail_content || []).slice(0, index),
                                { 
                                  ...detail, 
                                  content: { 
                                    ...detail.content,
                                    subtitle: e.target.value
                                  }
                                },
                                ...(newLocation.detail_content || []).slice(index + 1)
                              ];
                              setEditingLocation(newLocation);
                            }}
                            placeholder="Enter location subtitle..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Description</Label>
                          <Textarea
                            value={detail.content.description}
                            onChange={(e) => {
                              const newLocation = { ...editingLocation };
                              newLocation.detail_content = [
                                ...(newLocation.detail_content || []).slice(0, index),
                                { 
                                  ...detail, 
                                  content: { 
                                    ...detail.content,
                                    description: e.target.value
                                  }
                                },
                                ...(newLocation.detail_content || []).slice(index + 1)
                              ];
                              setEditingLocation(newLocation);
                            }}
                            placeholder="Enter location description..."
                          />
                        </div>
                      </div>
                    ) : (
                      <Textarea
                        value={JSON.stringify(detail.content, null, 2)}
                        onChange={(e) => {
                          try {
                            const newContent = JSON.parse(e.target.value);
                            const newLocation = { ...editingLocation };
                            newLocation.detail_content = [
                              ...(newLocation.detail_content || []).slice(0, index),
                              { ...detail, content: newContent },
                              ...(newLocation.detail_content || []).slice(index + 1)
                            ];
                            setEditingLocation(newLocation);
                          } catch (err) {
                            // Don't update if JSON is invalid
                          }
                        }}
                        placeholder="Enter content as JSON..."
                        className="font-mono text-sm mt-2"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  const newLocation = { ...editingLocation };
                  newLocation.detail_content = [
                    ...(newLocation.detail_content || []),
                    { type: "CHAT", content: "" }
                  ];
                  setEditingLocation(newLocation);
                }}
              >
                Add Detail Content
              </Button>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="mt-4">
          <Button
            onClick={() => {
              onSave(editingLocation);
              onClose();
            }}
          >
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
